/**
 * RegulationFlow — full-screen 3-question assessment overlay.
 *
 * UX principles (Apple onboarding-style):
 *   • One question per screen. Massive negative space. No scrolling.
 *   • Five taps to answer (no dropdown, no slider, no "next" button).
 *   • Immediate auto-advance on selection to keep the ~10s target.
 *   • On the last question → submit + reveal ring with slight suspense.
 *
 * Modal owns network I/O (fetch questions, submit assessment). Parent
 * only supplies the auth token and gets notified via onComplete().
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, ArrowRight, Loader2 } from "lucide-react";
import { REGULATION_SCALE, bandForScore } from "@/data/regulation";
import RegulationGauge from "./RegulationGauge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegulationFlow({
  open,
  archetype,
  token,
  onComplete,
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [scale, setScale] = useState(REGULATION_SCALE);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { score, band, assessment }
  const [error, setError] = useState(null);

  // Fetch archetype-specific questions when opened.
  useEffect(() => {
    if (!open || !archetype) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    setIdx(0);
    setAnswers({});
    setResult(null);
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/regulation/questions/${archetype}`,
        );
        if (!mounted) return;
        setQuestions(data.questions || []);
        setScale(data.scale || REGULATION_SCALE);
      } catch {
        if (mounted) setError("No pudimos cargar la evaluación. Intenta de nuevo.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, archetype]);

  const total = questions.length;
  const current = questions[idx];
  const progress = total > 0 ? ((idx + 1) / total) * 100 : 0;

  const handleAnswer = useCallback(
    async (question_id, value) => {
      const next = { ...answers, [question_id]: value };
      setAnswers(next);

      // Advance or submit.
      if (idx < total - 1) {
        // small delay to let the tap animation breathe
        setTimeout(() => setIdx((i) => i + 1), 220);
        return;
      }
      // Last question → submit
      setSubmitting(true);
      try {
        const payload = {
          answers: questions.map((q) => ({
            question_id: q.id,
            value: next[q.id],
          })),
        };
        const { data } = await axios.post(
          `${API}/regulation/submit`,
          payload,
          { headers: { "X-Access-Token": token } },
        );
        setResult({
          score: data.assessment.score,
          band: data.band,
          assessment: data.assessment,
        });
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            "No pudimos registrar tu evaluación. Intenta de nuevo.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [answers, idx, questions, total, token],
  );

  if (!open) return null;

  return (
    <div
      data-testid="regulation-flow"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] bg-[#050505] text-white flex flex-col animate-[regFadeIn_400ms_ease-out]"
      style={{ animationFillMode: "both" }}
    >
      {/* Subtle grid backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)",
          backgroundSize: "4px 4px",
        }}
      />
      {/* Teal halo */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.14) 0%, rgba(41,188,155,0) 60%)",
          filter: "blur(50px)",
        }}
      />

      {/* Top bar */}
      <div className="relative flex items-center justify-between px-6 sm:px-10 pt-6 sm:pt-8">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold">
          Tu punto de partida
        </div>
        <button
          onClick={onClose}
          data-testid="regulation-close"
          aria-label="Cerrar"
          className="w-9 h-9 rounded-full flex items-center justify-center text-white/40 hover:text-white/90 hover:bg-white/[0.06] transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Progress bar (hidden on result) */}
      {!result && !loading && !error && (
        <div className="relative mt-6 mx-6 sm:mx-10 h-[2px] bg-white/[0.06] rounded-full overflow-hidden">
          <div
            data-testid="regulation-progress"
            className="h-full bg-[#29BC9B] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Body */}
      <div className="relative flex-1 flex items-center justify-center px-6 sm:px-10 py-10">
        {loading && (
          <div className="flex flex-col items-center gap-4 text-white/60">
            <Loader2 className="animate-spin text-[#29BC9B]" size={28} />
            <span className="text-[12px] tracking-[0.22em] uppercase">
              Preparando tu evaluación
            </span>
          </div>
        )}

        {error && !loading && (
          <div className="max-w-md text-center space-y-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-red-400 font-semibold">
              Algo salió mal
            </div>
            <p className="text-[15px] text-white/70 leading-relaxed">{error}</p>
            <button
              onClick={onClose}
              className="text-[12px] tracking-[0.22em] uppercase text-white/60 hover:text-white transition-colors"
              data-testid="regulation-error-close"
            >
              Cerrar
            </button>
          </div>
        )}

        {!loading && !error && current && !result && !submitting && (
          <QuestionScreen
            question={current}
            index={idx}
            total={total}
            scale={scale}
            selected={answers[current.id] || null}
            onAnswer={(v) => handleAnswer(current.id, v)}
          />
        )}

        {submitting && (
          <div className="flex flex-col items-center gap-4 text-white/60">
            <Loader2 className="animate-spin text-[#29BC9B]" size={28} />
            <span className="text-[12px] tracking-[0.22em] uppercase">
              Calculando tu índice
            </span>
          </div>
        )}

        {result && (
          <ResultScreen
            result={result}
            onContinue={() => {
              onComplete?.(result);
              onClose?.();
            }}
          />
        )}
      </div>

      <style>{`
        @keyframes regFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes regRise {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─────────────── Question screen ─────────────── //
function QuestionScreen({ question, index, total, scale, selected, onAnswer }) {
  return (
    <div
      key={question.id}
      className="w-full max-w-[720px] mx-auto text-center"
      style={{ animation: "regRise 500ms ease-out both" }}
    >
      <div
        data-testid="regulation-question-index"
        className="text-[10px] tracking-[0.32em] uppercase text-white/40 font-semibold tabular-nums"
      >
        Pregunta {index + 1} de {total}
      </div>
      <h2
        data-testid="regulation-question-text"
        className="mt-6 text-2xl sm:text-[28px] font-semibold leading-[1.35] tracking-tight text-white max-w-[640px] mx-auto"
      >
        {question.text}
      </h2>

      <div className="mt-12 flex flex-col gap-2.5 max-w-[440px] mx-auto">
        {scale.map((opt) => {
          const isSelected = selected === opt.value;
          return (
            <button
              key={opt.value}
              data-testid={`regulation-answer-${opt.value}`}
              onClick={() => onAnswer(opt.value)}
              className={`group w-full flex items-center justify-between px-6 py-4 rounded-md border text-left transition-all duration-200 ${
                isSelected
                  ? "border-[#29BC9B] bg-[#29BC9B]/10 text-white"
                  : "border-white/[0.09] bg-white/[0.02] text-white/75 hover:border-white/25 hover:bg-white/[0.045] hover:text-white"
              }`}
            >
              <span className="text-[15px] font-medium tracking-tight">
                {opt.label}
              </span>
              <ArrowRight
                size={14}
                className={`transition-transform ${
                  isSelected
                    ? "translate-x-1 text-[#29BC9B]"
                    : "text-white/25 group-hover:translate-x-1 group-hover:text-white/60"
                }`}
              />
            </button>
          );
        })}
      </div>

      <p className="mt-10 text-[11px] tracking-[0.22em] uppercase text-white/30 font-medium">
        Elige la opción que refleje tu experiencia real bajo presión.
      </p>
    </div>
  );
}

// ─────────────── Result screen ─────────────── //
function ResultScreen({ result, onContinue }) {
  const band = result.band || bandForScore(result.score);
  return (
    <div
      data-testid="regulation-result"
      className="w-full max-w-[640px] mx-auto text-center"
      style={{ animation: "regRise 600ms ease-out both" }}
    >
      <div className="text-[10px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold">
        Tu punto de partida
      </div>
      <h2 className="mt-5 text-3xl sm:text-4xl font-semibold leading-tight tracking-tight">
        Índice de Regulación
        <br />
        <span className="text-white/60">Bajo Presión</span>
      </h2>

      <div className="mt-10 flex justify-center">
        <RegulationGauge
          value={result.score}
          size={260}
          thickness={14}
          label="Índice"
          sublabel={band.short || band.label}
          animate
        />
      </div>

      <p
        data-testid="regulation-band-narrative"
        className="mt-10 text-[15px] sm:text-base text-white/75 leading-[1.7] max-w-[520px] mx-auto"
      >
        {band.narrative}
      </p>

      <div className="mt-10 pt-8 border-t border-white/[0.06] max-w-[520px] mx-auto">
        <p className="text-[13px] text-white/45 leading-relaxed">
          Este es tu <span className="text-white/80">punto de partida</span>.
          En el Mapa Estratégico descubrirás qué activa tu patrón bajo presión
          y cómo desarrollar una mayor capacidad de regulación.
        </p>
        <button
          onClick={onContinue}
          data-testid="regulation-continue-btn"
          className="mt-8 group inline-flex items-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-8 py-4 rounded-sm transition-colors"
        >
          Continuar a mi evolución
          <ArrowRight
            size={15}
            className="group-hover:translate-x-1 transition-transform"
          />
        </button>
      </div>
    </div>
  );
}
